import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import { useAuthToken } from '../../hooks/useAuthToken';
import {
  fetchCampuses,
  fetchCampusData,
  fetchCampusEnergy,
  fetchSolarCampuses,
} from '../../api/campus';

export const useCampuses = (options?: { enabled?: boolean }) => {
  const client = useApiClient();
  const { hasToken } = useAuthToken();
  // Pulled out of the spread: options used to land last, so a caller-supplied
  // enabled would silently override the token gate.
  const { enabled: callerEnabled = true, ...restOptions } = options ?? {};

  return useQuery({
    queryKey: ['campus', 'list'],
    queryFn: () => fetchCampuses(client),
    // The campus list is stable for the session and every campus-scoped query
    // now waits on it, so a refetch must never re-open that gate.
    staleTime: Infinity,
    ...restOptions,
    enabled: hasToken && callerEnabled,
  });
};

export const useCampusData = (campusId: string) => {
  const client = useApiClient();
  const { hasToken } = useAuthToken();
  return useQuery({
    queryKey: ['campus', campusId, 'comfort'],
    queryFn: () => fetchCampusData(client, campusId),
    enabled: hasToken && !!campusId,
  });
};

export const useCampusEnergy = (campusId: string) => {
  const client = useApiClient();
  const { hasToken } = useAuthToken();
  return useQuery({
    queryKey: ['campus', campusId, 'energy'],
    queryFn: () => fetchCampusEnergy(client, campusId),
    enabled: hasToken && !!campusId,
  });
};

export const useSolarCampuses = () => {
  const client = useApiClient();
  const { hasToken } = useAuthToken();
  return useQuery({
    queryKey: ['campus', 'solar'],
    queryFn: () => fetchSolarCampuses(client),
    enabled: hasToken,
  });
};
